import React, { useState } from 'react';
import { Box, Typography, Button, Card, CardContent, Alert } from '@mui/material';
import { CameraIcon, QrCodeIcon } from '@heroicons/react/24/outline';
import MobileCameraHandler from '../components/common/MobileCameraHandler';
import { Scanner } from '@yudiel/react-qr-scanner';

const MobileCameraTest: React.FC = () => {
  const [scanning, setScanning] = useState(false);
  const [scannedData, setScannedData] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleScan = (result: string) => {
    console.log('QR Code scanned:', result);
    setScannedData(result);
    setScanning(false);
  };

  const handleError = (error: any) => {
    console.error('Scanner error:', error);
    setError('Scanner error: ' + error.message);
    setScanning(false);
  };

  const startScanning = () => {
    setScanning(true);
    setScannedData(null);
    setError(null);
  };

  const stopScanning = () => {
    setScanning(false);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Mobile Camera Test
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        This page tests mobile camera access for QR code scanning.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Camera Status
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">
              Protocol: {window.location.protocol}
            </Typography>
            <Typography variant="body2">
              User Agent: {navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop'}
            </Typography>
            <Typography variant="body2">
              Camera Support: {navigator.mediaDevices && navigator.mediaDevices.getUserMedia ? 'Yes' : 'No'}
            </Typography>
          </Box>

          {window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Camera access requires HTTPS. Please use HTTPS to test camera functionality.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            QR Code Scanner Test
          </Typography>
          
          {!scanning ? (
            <Box sx={{ textAlign: 'center' }}>
              <QrCodeIcon className="h-16 w-16 text-blue-500 mx-auto mb-4" />
              <Typography variant="body1" gutterBottom>
                Click the button below to test QR code scanning
              </Typography>
              <Button
                variant="contained"
                startIcon={<CameraIcon className="h-5 w-5" />}
                onClick={startScanning}
                sx={{ mt: 2 }}
              >
                Start QR Scanner
              </Button>
            </Box>
          ) : (
            <Box>
              <Box sx={{ 
                border: '2px solid #1976d2',
                borderRadius: 2,
                p: 1,
                textAlign: 'center',
                minHeight: '300px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <MobileCameraHandler
                  onPermissionGranted={() => {
                    console.log('Camera permission granted');
                  }}
                  onPermissionDenied={() => {
                    console.log('Camera permission denied');
                    setScanning(false);
                  }}
                >
                  <Scanner
                    onScan={handleScan}
                    onError={handleError}
                    constraints={{
                      facingMode: "environment",
                      width: { ideal: 1280 },
                      height: { ideal: 720 }
                    }}
                    styles={{
                      container: {
                        width: '100%',
                        height: '100%',
                        position: 'relative'
                      },
                      video: {
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }
                    }}
                    components={{
                      audio: false,
                      finder: true,
                      tracker: true
                    }}
                  />
                </MobileCameraHandler>
              </Box>
              
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={stopScanning}
                >
                  Stop Scanner
                </Button>
              </Box>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {scannedData && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>QR Code Scanned Successfully!</strong>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Data: {scannedData}
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Troubleshooting Tips
          </Typography>
          <Typography variant="body2" component="div">
            <ul>
              <li>Make sure you're using HTTPS (not HTTP)</li>
              <li>Grant camera permission when prompted</li>
              <li>Try refreshing the page if camera doesn't work</li>
              <li>Check your browser settings for camera permissions</li>
              <li>Ensure you're using a modern mobile browser</li>
            </ul>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MobileCameraTest;
